import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || '');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeSlug]);

  const category = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug],
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === activeSlug),
    [activeSlug],
  );

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category?.name || '分类' },
  ];

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category?.name || '分类'} - 精品久久久`,
    description: category?.description || '',
    url: `https://www.jingpin999.com/category/${activeSlug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://www.jingpin999.com/article/${a.slug}`,
        name: a.title,
      })),
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          {/* Header */}
          <section className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <div className="mb-4">
                <Breadcrumb items={breadcrumbItems} />
              </div>
              {category && (
                <>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500 text-background-50">
                      <i className={`${category.icon} text-xl`}></i>
                    </div>
                    <div>
                      <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900">
                        {category.name}
                      </h1>
                      <p className="text-sm text-foreground-500 mt-1">
                        {category.description}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-foreground-400 ml-16">
                    共 {category.articleCount} 篇文章
                  </p>
                </>
              )}
            </div>
          </section>

          {/* Category Switcher */}
          <section className="border-b border-background-200/70 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-1 overflow-x-auto py-3 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-background-100 hover:text-primary-600'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Articles Grid */}
          <section className="py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard
                      key={article.slug}
                      {...article}
                      imageUrl={article.imageUrl}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-file-search-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无文章</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}